import { ConversationMemberItem, useConversationMembers } from '@/features/conversation';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMessageReactionDialog } from '../hooks/use-message-reaction-dialog';
import { useMessageReactions } from '../queries/use-message-reactions';
import { EMOJI_REACTIONS } from './message-quick-actions/emoij-action';

// Re-export type for backward compatibility
export type { ShowMessageReactionsPayload } from '../models/message-reaction-dialog-types';

dayjs.extend(relativeTime);

interface MessageReactionDialogProps {
  conversationId: string;
}

export const MessageReactionDialog: React.FC<MessageReactionDialogProps> = ({ conversationId }) => {
  const { t } = useTranslation();
  const [selectedReaction, setSelectedReaction] = useState<string | null>(null);

  const { data, open, close } = useMessageReactionDialog();

  const { reactions, loadMore, isLoadingMore, canLoadMore, isLoading } = useMessageReactions({
    conversationId,
    messageId: data?.messageId || '',
    bucketId: data?.bucketId || '',
    pageSize: 100,
    enabled: open
  });

  const { data: members } = useConversationMembers(conversationId);

  const userMap = useMemo(() => new Map(members?.map((user) => [user.userId, user]) ?? []), [members]);

  const handleReactionFilter = (reaction: string | null) => {
    setSelectedReaction(reaction);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth='sm'
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxHeight: '80svh',
            minHeight: '60svh'
          }
        }
      }}
    >
      <DialogTitle className='mex:!p-2'>
        <Box className='mex:flex mex:items-center mex:justify-between'>
          <Typography variant='h6' component='div' className='mex:font-bold mex:text-gray-800'>
            {t('messages.reactions.dialogTitle')}
          </Typography>
          <IconButton onClick={close} size='small' color='error'>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className='mex:!p-0 mex:!overflow-hidden'>
        <Box className='mex:flex mex:h-full'>
          <ButtonGroup orientation='vertical' variant='outlined' className='mex:!p-2 mex:!border-r mex:!rounded-none'>
            <Button
              disableRipple
              onClick={() => handleReactionFilter(null)}
              className={`mex:!justify-start mex:!border-none mex:!rounded-lg mex:!mb-1 ${
                !selectedReaction ? 'mex:!bg-blue-50 mex:!text-blue-600 mex:!border-blue-200' : 'mex:!text-gray-700'
              }`}
            >
              <Stack direction='row' justifyContent='space-between' flex={1} component='div' gap={1}>
                <span>{t('messages.reactions.all')}</span>
                <span>{reactions.length}</span>
              </Stack>
            </Button>
            {EMOJI_REACTIONS.map(({ emoji }) => ({
              emoji,
              count: reactions.filter((r) => r.reaction === emoji).length
            }))
              .filter(({ count }) => count > 0)
              .sort((a, b) => b.count - a.count)
              .map(({ emoji, count }) => (
                <Button
                  key={emoji}
                  disableRipple
                  onClick={() => handleReactionFilter(emoji)}
                  className={`mex:!justify-start mex:!border-none mex:!rounded-lg mex:!mb-1 ${
                    selectedReaction === emoji
                      ? 'mex:!bg-blue-50 mex:!text-blue-600 mex:!border-blue-200'
                      : 'mex:!text-gray-700'
                  }`}
                >
                  <Stack direction='row' justifyContent='space-between' flex={1} component='div' gap={1}>
                    <span>{emoji}</span>
                    <span>{count}</span>
                  </Stack>
                </Button>
              ))}
          </ButtonGroup>

          <Box className='mex:flex mex:flex-1 mex:flex-col'>
            <Box className='mex:flex-1 mex:overflow-y-auto'>
              {isLoading ? (
                <Box className='mex:p-4 mex:text-center'>
                  <Typography variant='body2' color='text.secondary'>
                    {t('messages.reactions.loading')}
                  </Typography>
                </Box>
              ) : reactions.length === 0 ? (
                <Box className='mex:p-4 mex:text-center'>
                  <Typography variant='body2' color='text.secondary'>
                    {selectedReaction
                      ? t('messages.reactions.noReactionsWithEmoji', { emoji: selectedReaction })
                      : t('messages.reactions.noReactions')}
                  </Typography>
                </Box>
              ) : (
                <Box className='mex:p-2'>
                  {reactions
                    .filter((r) => r.reaction === selectedReaction || !selectedReaction)
                    .map((reaction) => {
                      const user = userMap.get(reaction.userId);
                      return user ? (
                        <ConversationMemberItem
                          key={reaction.userId}
                          member={user}
                          secondaryAction={reaction.reaction}
                        />
                      ) : null;
                    })}

                  {/* Load more button */}
                  {canLoadMore && (
                    <Box className='mex:p-4 mex:text-center'>
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={loadMore}
                        disabled={isLoadingMore}
                        className='mex:!rounded-full'
                      >
                        {isLoadingMore ? t('messages.reactions.loadingMore') : t('messages.reactions.loadMore')}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
